import { defineStore } from 'pinia'
import { DateTime, Interval } from 'luxon'
import useUserStore from '~~/app/store/user'
import { ObjectState } from '~~/interfaces/poolDetail'
import type {
    Network,
    Owner,
    Pool,
    PoolAccess,
    PoolStatistic
} from '~~/interfaces/poolDetail'
import { formatRating } from '~~/app/utils/format'
import type { Feedback, FeedbackType } from '~~/interfaces/feedback'
import type {
    PoolStation,
    PoolStationConnectorForDisplay,
    PoolStationConnectorRealtimeStatusCount,
    PoolStationEvse
} from '~~/interfaces/station'
import { $fetchApi, $fetchApiRaw } from '~~/app/composables/fetchApi'
import { PhotoCategory } from '#cmui/types'
import type { PoolDetail, PoolPhoto, PoolStationConnector } from '#cmui/types'
import type { PoolDetailFromMappy } from '~~/interfaces/city'
import type { MapPool } from '~~/interfaces/map'

export const usePoolStore = defineStore('pool', () => {
    const baseUrl = tryUseNuxtApp()
        ? useRuntimeConfig().public.APPLICATION_URL
        : process.env.NUXT_PUBLIC_APPLICATION_URL

    const communityFeedbacksLoader = ref(false)
    const communityPoolPhotosLoader = ref(true)
    const communityPoolAccessLoader = ref(true)
    const communityPoolStationsLoader = ref(true)
    const poolDetailLoader = ref(true)
    const mappyLoader = ref(true)
    const pricesLoader = ref(true)
    const slug = ref('')
    const poolId = ref(0)
    const poolName = ref('')
    const canChargeWithChargemapPass = ref(false)
    const isRemoteCompatible = ref(false)
    const poolDetail = ref<Pool | null>(null)
    const statistics = ref<PoolStatistic | null>(null)
    const filteredFeedbacks = ref<Feedback[] | null>([])
    const unfilteredFeedbacks = ref<Feedback[] | null>([])
    const totalFeedbacksCount = ref(0)
    const feedbacksFilter = ref<FeedbackType | ''>('')
    const feedbacksMinIndex = ref(0)
    const feedbacksLimit = ref(20)
    const poolStations = ref<PoolStation[] | null>([])
    const nearStations = ref<MapPool[] | null>([])
    const poolConnectors = ref<PoolStationConnectorForDisplay[] | null>([])
    const prices = ref('')
    const poolPhotos = ref<PoolPhoto[]>([])
    const poolAccess = ref<PoolAccess | null>(null)

    const getPoolDetailForMapDrawer = computed<PoolDetail>(() => {
        return keysToCamel(poolDetail.value)
    })

    const getCoordinates = computed<{
        latitude: string | null
        longitude: string | null
    }>(() => {
        return {
            latitude: poolDetail.value?.coordinates?.lat || null,
            longitude: poolDetail.value?.coordinates?.lon || null
        }
    })

    const getOwner = computed<Owner | null>(() => {
        return poolDetail.value?.owner || null
    })

    const getOwnerName = computed<string>(() => {
        return poolDetail.value?.owner?.name || ''
    })

    const isTeslaCompatible = computed<boolean>(() => {
        return poolDetail.value?.isTesla || false
    })

    const getGlobalScoreAverage = computed<string>(() => {
        return (
            (statistics.value?.globalNoteAverage &&
                formatRating(statistics.value.globalNoteAverage)) ||
            ''
        )
    })

    const getMaterialScoreAverage = computed<string>(() => {
        return (
            (statistics.value?.materialNoteAverage &&
                formatRating(statistics.value.materialNoteAverage)) ||
            ''
        )
    })

    const getPriceScoreAverage = computed<string>(() => {
        return (
            (statistics.value?.priceNoteAverage &&
                formatRating(statistics.value.priceNoteAverage)) ||
            ''
        )
    })

    const getLocationScoreAverage = computed<string>(() => {
        return (
            (statistics.value?.locationNoteAverage &&
                formatRating(statistics.value.locationNoteAverage)) ||
            ''
        )
    })

    const getSecurityScoreAverage = computed<string>(() => {
        return (
            (statistics.value?.securityNoteAverage &&
                formatRating(statistics.value.securityNoteAverage)) ||
            ''
        )
    })

    const hasAtLeastOneNewFeedback = computed<boolean>(() => {
        const stats = statistics.value || {
            checkinsCount: 0,
            reportsCount: 0,
            commentsCount: 0,
            ratingsCount: 0,
            globalNoteAverage: 0,
            materialNoteAverage: 0,
            priceNoteAverage: 0,
            locationNoteAverage: 0,
            securityNoteAverage: 0,
            creationDate: ''
        }

        return (
            stats?.materialNoteAverage !== 0 &&
            stats?.priceNoteAverage !== 0 &&
            stats?.locationNoteAverage !== 0 &&
            stats?.securityNoteAverage !== 0
        )
    })

    const getStreetNumber = computed<string | null>(() => {
        return poolDetail.value?.number || null
    })

    const getStreetName = computed<string>(() => {
        return poolDetail.value?.streetName || ''
    })

    const getPostalCode = computed<string>(() => {
        return poolDetail.value?.postalCode || ''
    })

    const getCity = computed<string>(() => {
        return poolDetail.value?.city || ''
    })

    const getCountryName = computed<string>(() => {
        return poolDetail.value?.countryName || ''
    })

    const getCountryCode = computed<string>(() => {
        return poolDetail.value?.countryCode || ''
    })

    const getDirectionLink = computed<string>(() => {
        let poolAddress = ''
        poolAddress = `${
            (poolDetail.value?.number ? `${poolDetail.value?.number} ` : '') +
                poolDetail.value?.countryName || ''
        }`
        poolAddress += `${
            (poolDetail.value?.postalCode
                ? `${poolDetail.value?.postalCode} `
                : '') + poolDetail.value?.city || ''
        } `
        poolAddress += poolDetail.value?.countryName || ''

        return `https://www.google.com/maps?q=${encodeURIComponent(
            poolAddress
        )}`
    })

    const isIndoor = computed<boolean>(() => {
        return poolDetail.value?.indoor || false
    })

    const getWarningMessage = computed<string>(() => {
        return poolDetail.value?.network?.warningMessage || ''
    })

    const isDeleted = computed<boolean>(() => {
        return poolDetail.value?.state === ObjectState.DELETED || false
    })

    const getDescription = computed<string>(() => {
        return poolDetail.value?.description || ''
    })

    const getNetwork = computed<Network | null>(() => {
        return poolDetail.value?.network || null
    })

    const getNetworkName = computed<string>(() => {
        return poolDetail.value?.network?.name || ''
    })

    const getRating = computed<string>(() => {
        return (
            statistics.value?.globalNoteAverage?.toFixed(1)?.toString() || '0'
        )
    })

    const getRatingCount = computed<number>(() => {
        return statistics.value?.ratingsCount || 0
    })

    const hasActiveDiscount = computed<boolean>(() => {
        const networkPromotion = poolDetail.value?.network?.promotion

        if (
            !networkPromotion ||
            !networkPromotion?.startDate ||
            !networkPromotion?.endDate
        )
            return false

        return Interval.fromDateTimes(
            DateTime.fromISO(networkPromotion.startDate as string),
            DateTime.fromISO(networkPromotion.endDate as string)
        ).contains(DateTime.now())
    })

    const getDiscountAmount = computed<number | null>(() => {
        return poolDetail.value?.network?.promotion?.percentageDiscount || null
    })

    const getDiscountEndDate = computed<string | null>(() => {
        return poolDetail.value?.network?.promotion?.endDate
            ? DateTime.fromISO(poolDetail.value.network.promotion.endDate)
                  .minus({ days: 1 })
                  .toFormat('d/M')
            : null
    })

    const isAnOldFeedback = (feedback: Feedback): boolean => {
        return (
            DateTime.fromISO(feedback.creationDate) <
            DateTime.now().minus({ days: 90 })
        )
    }

    const setDisplayShowOlderCommentsLabelProp = () => {
        let hasSetOldCommentProp = false

        const feedbacksToUpdate =
            feedbacksFilter.value !== ''
                ? filteredFeedbacks.value
                : unfilteredFeedbacks.value
        feedbacksToUpdate?.forEach((feedback: Feedback) => {
            if (isAnOldFeedback(feedback) && !hasSetOldCommentProp) {
                feedback.displayShowOlderCommentsLabel = true
                hasSetOldCommentProp = true
            } else {
                feedback.displayShowOlderCommentsLabel = false
            }
        })
    }

    const setPoolConnectors = () => {
        const allConnectors = [] as PoolStationConnectorForDisplay[]
        poolStations.value?.forEach((poolStation: PoolStation) => {
            poolStation.evses?.forEach((evse: PoolStationEvse) => {
                evse.connectors?.forEach((connector: PoolStationConnector) => {
                    const connectorAlreadyAddedIndex = allConnectors.findIndex(
                        (
                            poolStationConnector: PoolStationConnectorForDisplay
                        ) =>
                            poolStationConnector.connectorType?.id ===
                                connector.connectorType?.id &&
                            poolStationConnector.power === connector.power
                    )

                    if (connectorAlreadyAddedIndex !== -1) {
                        const realtimeStatusAlreadyAddedIndex = allConnectors[
                            connectorAlreadyAddedIndex
                        ]!.realtimeStatusCount.findIndex(
                            (
                                realtimeStatusCount: PoolStationConnectorRealtimeStatusCount
                            ) =>
                                realtimeStatusCount.realtimeStatus ===
                                connector.realtimeStatus
                        )

                        if (realtimeStatusAlreadyAddedIndex !== -1) {
                            allConnectors[
                                connectorAlreadyAddedIndex
                            ]!.realtimeStatusCount[
                                realtimeStatusAlreadyAddedIndex
                            ]!.count += 1
                        } else {
                            allConnectors[
                                connectorAlreadyAddedIndex
                            ]!.realtimeStatusCount.push({
                                realtimeStatus: connector.realtimeStatus,
                                count: 1
                            })
                        }
                    } else {
                        allConnectors.push({
                            ...connector,
                            realtimeStatusCount: [
                                {
                                    realtimeStatus: connector.realtimeStatus,
                                    count: 1
                                }
                            ]
                        })
                    }
                })
            })
        })

        poolConnectors.value = allConnectors
    }

    const setPoolPhotos = (poolPictures: PoolPhoto[]) => {
        poolPhotos.value = poolPictures.filter(
            (photo) => photo.category !== PhotoCategory.COVER
        )
    }

    const fetchChargingCompatibilitiesFromMappy = async () => {
        const { $throwError } = useNuxtApp()
        try {
            const params = {
                ...getCoordinates.value,
                limit: 1,
                state: useUserStore().getUserRankAsParam
            }
            mappyLoader.value = true

            const data: any = await $fetchApi(
                `${baseUrl}/mappy/charging_pools.json`,
                {
                    params,
                    skipTokenRefresh: true
                }
            )

            const pool = data?.response?.content?.items[0]
                ?.pool as PoolDetailFromMappy

            if (pool) {
                canChargeWithChargemapPass.value =
                    pool?.canChargeWithChargemap || false
                isRemoteCompatible.value = pool?.canRemoteStartCharge || false
            }
        } catch (e: any) {
            $throwError(
                e,
                `ResourceNotFound: fetchChargingCompatibilitiesFromMappy (${
                    poolId.value || 'unknown pool'
                })`
            )
        } finally {
            mappyLoader.value = false
        }
    }

    const fetchPoolDetailFromPoolDetail = async () => {
        const { $throwError, $i18n } = useNuxtApp()

        try {
            const params = {
                locale: $i18n.locale.value
            }
            poolDetailLoader.value = true

            const data = await $fetchApi<Pool>(
                `${baseUrl}/pool-detail/v2/pools/${slug.value}`,
                {
                    params,
                    skipTokenRefresh: true
                }
            )
            poolDetail.value = data
            poolName.value = data?.name || ''
            poolId.value = data?.id || 0
            statistics.value = data?.statistic || null
        } catch (e: any) {
            $throwError(
                e,
                `ResourceNotFound: fetchPoolDetailFromPoolDetail (${
                    poolId.value || 'unknown pool'
                })`
            )
        } finally {
            poolDetailLoader.value = false
        }
    }

    const fetchStatisticsFromCommunityFeedbacks = async () => {
        const { $throwError } = useNuxtApp()
        try {
            communityFeedbacksLoader.value = true

            statistics.value = await $fetchApi<PoolStatistic>(
                `${baseUrl}/community-feedbacks/pools/${poolId.value}/statistics/latest`,
                { skipTokenRefresh: true }
            )
        } catch (e: any) {
            $throwError(
                e,
                `ResourceNotFound: fetchStatisticsFromCommunityFeedbacks (${
                    poolId.value || 'unknown pool'
                })`
            )
        } finally {
            communityFeedbacksLoader.value = false
        }
    }

    const fetchFeedbacksFromCommunityFeedbacks = async () => {
        const { $throwError } = useNuxtApp()

        if (poolId.value) {
            try {
                const params = {
                    moderation_status: 'VALIDATED,PENDING',
                    pool_id: poolId.value,
                    offset: feedbacksMinIndex.value,
                    limit: feedbacksLimit.value,
                    feedback_type: feedbacksFilter.value || ''
                }

                // Only display loader when requesting full list
                if (feedbacksMinIndex.value === 0) {
                    communityFeedbacksLoader.value = true
                }

                const response = await $fetchApiRaw<any>(
                    `${baseUrl}/community-feedbacks/feedbacks`,
                    {
                        params,
                        skipTokenRefresh: true
                    }
                )

                const lastFetchedFeedbacks =
                    (response._data && response._data.items) || []

                totalFeedbacksCount.value = Number(
                    response?.headers?.get('x-total-count') || '0'
                )

                if (feedbacksFilter.value) {
                    filteredFeedbacks.value =
                        feedbacksMinIndex.value === 0
                            ? lastFetchedFeedbacks
                            : [
                                  ...(filteredFeedbacks.value || []),
                                  ...lastFetchedFeedbacks
                              ]
                } else {
                    unfilteredFeedbacks.value =
                        feedbacksMinIndex.value === 0
                            ? lastFetchedFeedbacks
                            : [
                                  ...(unfilteredFeedbacks.value || []),
                                  ...lastFetchedFeedbacks
                              ]
                }
                setDisplayShowOlderCommentsLabelProp()
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: fetchFeedbacksFromCommunityFeedbacks (${poolId.value || 'unknown pool'})`
                )
            } finally {
                communityFeedbacksLoader.value = false
            }
        }
    }

    const postCommentToCommunityFeedbacks = async (
        comment: string,
        isNonAnonymous: boolean
    ) => {
        if (!useUserSession().user?.value?.id) {
            throw new Error('User not logged in')
        }

        const { $throwError } = useNuxtApp()

        try {
            const params = {
                pool_id: poolId.value,
                user_id: Number(useUserSession().user!.value!.id),
                is_anonymous: !isNonAnonymous,
                comment
            }

            await $fetchApi(`${baseUrl}/community-feedbacks/comments`, {
                method: 'POST',
                body: params
            })

            filteredFeedbacks.value = []
            unfilteredFeedbacks.value = []
            await fetchFeedbacksFromCommunityFeedbacks()
            await fetchStatisticsFromCommunityFeedbacks()
        } catch (e: any) {
            $throwError(
                e,
                `ResourceNotFound: postCommentToCommunityFeedbacks (${
                    poolId.value || 'unknown pool'
                })`
            )
        }
    }

    const fetchPoolStationsFromCommunity = async () => {
        const { $throwError } = useNuxtApp()

        if (poolId.value) {
            try {
                const params = {
                    locale: useLanguage()
                }
                communityPoolStationsLoader.value = true

                const data = await $fetchApi<{ stations: PoolStation[] }>(
                    `${baseUrl}/community/pool/${poolId.value}/stations.json`,
                    {
                        params,
                        skipTokenRefresh: true
                    }
                )
                poolStations.value = data?.stations || []
                setPoolConnectors()
            } catch (e: any) {
                $throwError(
                    e,
                    `ResourceNotFound: fetchPoolStationsFromCommunity (${
                        poolId.value || 'unknown pool'
                    })`
                )
            } finally {
                communityPoolStationsLoader.value = false
            }
        }
    }

    const fetchNearStations = async () => {
        const { $throwError } = useNuxtApp()
        try {
            const params = {
                ...getCoordinates.value,
                limit: 6,
                state: useUserStore().getUserRankAsParam
            }
            mappyLoader.value = true

            const data = await $fetchApi<{
                response?: {
                    content?: {
                        items: MapPool[]
                    }
                }
            }>(`${baseUrl}/mappy/charging_pools.json`, {
                params,
                skipTokenRefresh: true
            })
            nearStations.value = data?.response?.content?.items || []
        } catch (e: any) {
            $throwError(
                e,
                `ResourceNotFound: getNear (${poolId.value || 'unknown pool'})`
            )
        } finally {
            mappyLoader.value = false
        }
    }

    const fetchPrices = async (noFooter: boolean = false) => {
        const { $throwError } = useNuxtApp()
        try {
            const params = {
                locale: useLanguage(),
                noBootstrap: true,
                noFooter
            }
            pricesLoader.value = true

            prices.value = await $fetchApi<string>(
                `${baseUrl}/pool-prices/v3/prices/${poolId.value}`,
                {
                    params,
                    skipTokenRefresh: !useUserSession().user?.value?.id
                },
                false
            )
        } catch (e: any) {
            if (e.response?.status !== 404) {
                $throwError(
                    e,
                    `ResourceNotFound: getPrices (${
                        poolId.value || 'unknown pool'
                    })`
                )
            }
            prices.value = e.response?.data || ''
        } finally {
            pricesLoader.value = false
        }
    }

    const fetchPoolPhotos = async () => {
        const { $throwError } = useNuxtApp()

        try {
            const params = {
                poolId: poolId.value
            }
            communityPoolPhotosLoader.value = true

            const data = await $fetchApi<PoolPhoto[]>(
                `${baseUrl}/community-photos/photos/pools`,
                {
                    params,
                    skipTokenRefresh: true
                }
            )

            setPoolPhotos(data)
        } catch (e: any) {
            $throwError(
                e,
                `ResourceNotFound: fetchPoolPictures (${
                    poolId.value || 'unknown pool'
                })`
            )
        } finally {
            communityPoolPhotosLoader.value = false
        }
    }

    const fetchPoolAccess = async () => {
        const { $throwError } = useNuxtApp()
        try {
            const params = {
                locale: useLanguage()
            }
            communityPoolAccessLoader.value = true

            poolAccess.value = await $fetchApi<PoolAccess>(
                `${baseUrl}/community/pool/${poolId.value}/access.json`,
                {
                    params
                }
            )
        } catch (e: any) {
            $throwError(
                e,
                `ResourceNotFound: fetchPoolAccess (${
                    poolId.value || 'unknown pool'
                })`
            )
        } finally {
            communityPoolAccessLoader.value = false
        }
    }

    const updateConnectorState = async (
        isUsable: number,
        stationId: number,
        connectorId: number
    ) => {
        const { $throwError } = useNuxtApp()
        try {
            const params = {
                is_usable: isUsable
            }
            await $fetchApi(
                `${baseUrl}/legacy-api/v2/charging_stations/${stationId}/connectors/${connectorId}.json`,
                {
                    method: 'PUT',
                    params
                }
            )
        } catch (e: any) {
            $throwError(
                e,
                `RequestFailed: updateConnectorState (${
                    stationId || 'unknown station'
                })`
            )
        }
    }

    const updateStation = async (valueToUpdate: number, stationId: number) => {
        const { $throwError } = useNuxtApp()
        try {
            await $fetchApi(
                `${baseUrl}/legacy-api/v2/charging_pools/${poolId.value}/stations/${stationId}.json`,
                {
                    method: 'PUT',
                    params: {
                        charging_administrative_state_id: valueToUpdate
                    }
                }
            )
        } catch (e: any) {
            $throwError(
                e,
                `RequestFailed: updateStation (${
                    poolId.value || 'unknown pool'
                })`
            )
        }
    }

    const sendRotatedPicture = async (picture: any, blob: Blob) => {
        const formData = new FormData()
        formData.append('url', blob, picture.url)

        return $fetchApi(
            `${baseUrl}/legacy-api/v2/charging_pools/${poolId.value}/photos/${picture.id}.json`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
                // formData //TODO restore when feature is brought back in
            }
        )
    }

    return {
        communityFeedbacksLoader,
        communityPoolPhotosLoader,
        communityPoolAccessLoader,
        communityPoolStationsLoader,
        poolDetailLoader,
        mappyLoader,
        pricesLoader,
        slug,
        poolId,
        poolName,
        canChargeWithChargemapPass,
        isRemoteCompatible,
        poolDetail,
        statistics,
        filteredFeedbacks,
        unfilteredFeedbacks,
        totalFeedbacksCount,
        feedbacksFilter,
        feedbacksMinIndex,
        feedbacksLimit,
        poolStations,
        nearStations,
        poolConnectors,
        prices,
        poolPhotos,
        poolAccess,
        getPoolDetailForMapDrawer,
        getCoordinates,
        getOwner,
        getOwnerName,
        isTeslaCompatible,
        getGlobalScoreAverage,
        getMaterialScoreAverage,
        getPriceScoreAverage,
        getLocationScoreAverage,
        getSecurityScoreAverage,
        hasAtLeastOneNewFeedback,
        getStreetNumber,
        getStreetName,
        getPostalCode,
        getCity,
        getCountryName,
        getCountryCode,
        getDirectionLink,
        isIndoor,
        getWarningMessage,
        isDeleted,
        getDescription,
        getNetwork,
        getNetworkName,
        getRating,
        getRatingCount,
        hasActiveDiscount,
        getDiscountAmount,
        getDiscountEndDate,
        isAnOldFeedback,
        setDisplayShowOlderCommentsLabelProp,
        setPoolConnectors,
        setPoolPhotos,
        fetchChargingCompatibilitiesFromMappy,
        fetchPoolDetailFromPoolDetail,
        fetchStatisticsFromCommunityFeedbacks,
        fetchFeedbacksFromCommunityFeedbacks,
        postCommentToCommunityFeedbacks,
        fetchPoolStationsFromCommunity,
        fetchNearStations,
        fetchPrices,
        fetchPoolPhotos,
        fetchPoolAccess,
        updateConnectorState,
        updateStation,
        sendRotatedPicture
    }
})

type PoolStore = Omit<
    ReturnType<typeof usePoolStore>,
    keyof ReturnType<typeof defineStore>
>

export default usePoolStore

export type { PoolStore }
